// src/components/HeroCSS.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "./HeroCSS.css";
import videoSrc from "./images/intro.webm";

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const videoRef = useRef(null);
  const observerRef = useRef(null);

  const [videoVisible, setVideoVisible] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  // Play muted on mount
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.muted = true;
    video.play().catch(() => {}); // handle autoplay restrictions
  }, []);

  // Enable sound on first touch/click if video is visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleInteraction = () => {
      const rect = video.getBoundingClientRect();
      const fullyVisible =
        rect.top >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

      if (!fullyVisible) return; // only unmute if fully on screen

      video.muted = false;
      setUserInteracted(true);
      video.play().catch(() => {});
    };

    window.addEventListener("click", handleInteraction);
    window.addEventListener("touchstart", handleInteraction);

    return () => {
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
    };
  }, []);

  // Play/pause based on visibility using IntersectionObserver
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const fullyVisible = entry.intersectionRatio >= 0.99;

        if (fullyVisible) {
          setVideoVisible(true);
          video.play().catch(() => {});
        } else {
          setVideoVisible(false);
          video.pause();
        }
      },
      { threshold: 0.99 }
    );

    observerRef.current.observe(video);

    return () => observerRef.current?.disconnect();
  }, []);

  // Framer Motion scroll effects
  const y = useTransform(scrollYProgress, [0, 0.3], ["0vh", "-90vh"]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <section className="hero">
      <div className="hero-container">
        {/* TEXT */}
        <motion.div
          className="hero-text"
          style={{ opacity }}
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
        >
          <h1>
            Welcome to <span>AnkieLab</span>
          </h1>
          <h3 className="hero-heading">
            <strong>WHERE CREATIVITY MEETS DIMENSION</strong>
          </h3>
          <p>From concept to render | Build your 3D vision with precision.</p>

          <div className="hero-buttons">
            <motion.a href="#services" className="btn">
              Our Services
            </motion.a>
            <motion.a href="#contact" className="btn btn-outline">
              Contact Us
            </motion.a>
          </div>
        </motion.div>

        {/* VIDEO */}
        <motion.div
          className="hero-video"
          style={{ y, scale, opacity }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
        >
          <motion.video
            ref={videoRef}
            src={videoSrc}
            loop
            playsInline
            autoPlay
            muted={!userInteracted}
            preload="auto"
            className="hero-video-element"
            animate={{ opacity: videoVisible ? 1 : 0.3 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      </div>
    </section>
  );
}
